import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, Calendar, Flag, User, Tag, Trash2, Check, ChevronLeft, ChevronRight, Plus, Bell } from 'lucide-react';
import { Todo } from '../../../hooks/useSupabaseKanban';
import { SpaceMember } from '../../../contexts/SpaceContext';
import { useFocusTrap } from '../../../hooks/useFocusTrap';
import { saveTaskDraft, loadTaskDraft, clearTaskDraft } from '../../../utils/formDraft';
import { Tag as ContentTag } from '../../../hooks/useTags';
import { useTimeFormat, useDateFormat, formatShortDate, DateFormat } from '../../settings/TimePicker';
import { InlineTimeInput, EndTimeInput } from './InlineTimeInput';
import { DateTextInput } from './DateTextInput';

interface TodoFormModalProps {
  isOpen: boolean;
  todo: Todo | null;
  members: SpaceMember[];
  isPersonalSpace: boolean;
  defaultColumnId?: string;
  defaultDueDate?: string;
  defaultTitle?: string;
  position?: { x: number; y: number } | null;
  restoreFromDraftOnOpen?: boolean;

  contentTags?: ContentTag[];
  selectedContentTags?: ContentTag[];
  onContentTagsChange?: (tags: ContentTag[]) => void;
  onCreateContentTag?: (name: string) => Promise<ContentTag | null>;

  onSave: (todoData: {
    title: string;
    notes: string | null;
    column_id: string;
    due_date: string | null;
    due_time: string | null;
    duration: number | null;
    calendar_id: string | null;
    assignee_id: string | null;
    priority: 'none' | 'low' | 'medium' | 'high';
    reminder_minutes_before: number | null;
    reminder_fire_at: string | null;
  }) => Promise<void>;
  onToggleComplete?: () => Promise<void>;
  onDelete: () => Promise<void>;
  onArchive: () => Promise<void>;
  onUnarchive: () => Promise<void>;
  onClose: () => void;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const PRIORITY_ORDER = ['low', 'medium', 'high'] as const;
const PRIORITY = {
  low:    { label: 'Low',    color: 'var(--accent-primary)'      },
  medium: { label: 'Medium', color: 'var(--status-warning-text)' },
  high:   { label: 'High',   color: 'var(--status-error-text)'   },
} as const;

const MONTHS = ['January','February','March','April','May','June',
  'July','August','September','October','November','December'];
const DAYS = ['Mo','Tu','We','Th','Fr','Sa','Su'];

function toDateKey(y: number, m: number, d: number) {
  return `${y}-${String(m+1).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
}
function daysInMonth(y: number, m: number) { return new Date(y, m+1, 0).getDate(); }
function firstWeekday(y: number, m: number) {
  const d = new Date(y, m, 1).getDay();
  return d === 0 ? 6 : d - 1;
}
function formatDateBadge(str: string, dateFormat: DateFormat) {
  const parts = str.split('-');
  if (parts.length !== 3) return str;
  const d = new Date(Number(parts[0]), Number(parts[1])-1, Number(parts[2]));
  const today = new Date(); today.setHours(0,0,0,0);
  const diff = Math.round((d.getTime() - today.getTime()) / 86400000);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Tomorrow';
  return formatShortDate(str, dateFormat);
}
function autoDateForTime(timeStr: string): string {
  const [h, m] = timeStr.split(':').map(Number);
  const now = new Date();
  const todayMins = now.getHours() * 60 + now.getMinutes();
  const targetMins = h * 60 + m;
  // If the time has already passed today, schedule for tomorrow
  const d = targetMins > todayMins ? new Date() : new Date(Date.now() + 86400000);
  return toDateKey(d.getFullYear(), d.getMonth(), d.getDate());
}
function getTodayKey(): string {
  const d = new Date();
  return toDateKey(d.getFullYear(), d.getMonth(), d.getDate());
}
function getRoundedTime() {
  const now = new Date();
  const m = now.getMinutes();
  const rounded = m < 30 ? 30 : 0;
  const h = rounded === 0 ? now.getHours() + 1 : now.getHours();
  return `${String(h % 24).padStart(2,'0')}:${String(rounded).padStart(2,'0')}`;
}
function formatTimeBadge(t: string, is24h: boolean) {
  const [h, m] = t.split(':').map(Number);
  if (is24h) return `${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}`;
  const period = h >= 12 ? 'PM' : 'AM';
  const display = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${display}:${String(m).padStart(2,'0')} ${period}`;
}
function addMinutesToTime(hhmm: string, minutes: number): string {
  const [h, m] = hhmm.split(':').map(Number);
  const total = h * 60 + m + minutes;
  return `${String(Math.floor(total / 60) % 24).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
}

type ReminderUnit = 'minute' | 'hour' | 'day' | 'week' | 'month';

const UNIT_MINUTES: Record<ReminderUnit, number> = {
  minute: 1,
  hour: 60,
  day: 60 * 24,
  week: 60 * 24 * 7,
  month: 60 * 24 * 30,
};

const REMINDER_PRESETS = [
  { label: 'At time of task', value: 0 },
  { label: '15 min before',   value: 15 },
  { label: '30 min before',   value: 30 },
  { label: '1 hr before',     value: 60 },
] as const;

function formatReminderChip(minutes: number): string {
  if (minutes === 0) return 'On time';
  if (minutes < 60) return `${minutes}m before`;
  const h = minutes / 60;
  if (h < 24) return `${Number.isInteger(h) ? h : h.toFixed(1)}h before`;
  const d = minutes / (60 * 24);
  if (d < 7) return `${Number.isInteger(d) ? d : d.toFixed(1)}d before`;
  const w = minutes / (60 * 24 * 7);
  return `${Number.isInteger(w) ? w : w.toFixed(1)}w before`;
}

function computeReminderFireAt(
  dueDate: string,
  dueTime: string | null,
  minutesBefore: number
): string {
  const [y, m, d] = dueDate.split('-').map(Number);
  const [h, min] = dueTime ? dueTime.split(':').map(Number) : [9, 0]; // default 9am
  const dueAt = new Date(y, m - 1, d, h, min, 0, 0); // interprets as local time
  return new Date(dueAt.getTime() - minutesBefore * 60_000).toISOString();
}

function minutesToCustomFields(minutes: number): { value: number; unit: ReminderUnit } {
  const order: ReminderUnit[] = ['month', 'week', 'day', 'hour', 'minute'];
  for (const unit of order) {
    const mult = UNIT_MINUTES[unit];
    if (minutes >= mult && minutes % mult === 0) return { value: minutes / mult, unit };
  }
  return { value: minutes, unit: 'minute' };
}

// ─── Metadata chip button ─────────────────────────────────────────────────────

interface ChipProps {
  icon: React.ReactNode;
  label?: string | null;
  active?: boolean;
  activeColor?: string;
  placeholder: string;
  onClick: () => void;
}

const MetaChip = React.forwardRef<HTMLButtonElement, ChipProps>(
  ({ icon, label, active, activeColor, placeholder, onClick }, ref) => (
    <button
      ref={ref}
      type="button"
      onClick={onClick}
      style={active && activeColor ? { color: activeColor } : undefined}
      className={`inline-flex items-center gap-1.5 h-7 px-2.5 rounded-lg text-xs transition-colors border ${
        active
          ? 'border-[var(--border-primary)] bg-[var(--surface-tertiary)] text-[var(--content-secondary)]'
          : 'border-transparent text-[var(--content-tertiary)] hover:border-[var(--border-secondary)] hover:bg-[var(--surface-tertiary)] hover:text-[var(--content-secondary)]'
      }`}
    >
      {icon}
      <span>{active && label ? label : placeholder}</span>
    </button>
  )
);

// ─── Portal popover ───────────────────────────────────────────────────────────

interface PopoverProps {
  style: React.CSSProperties;
  width?: number;
  children: React.ReactNode;
  onClose: () => void;
  ignoreRef: React.RefObject<HTMLElement | null>;
}

const Popover: React.FC<PopoverProps> = ({ style, width = 200, children, onClose, ignoreRef }) => {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const h = (e: MouseEvent) => {
      const t = e.target as Node;
      const onPortal = !!(t as Element).closest?.('[data-tag-input-portal]');
      if (!ref.current?.contains(t) && !ignoreRef.current?.contains(t) && !onPortal) onClose();
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [onClose, ignoreRef]);

  return createPortal(
    <div
      ref={ref}
      data-tag-input-portal
      style={{ ...style, width, boxShadow: 'var(--shadow-lg)' }}
      className="fixed bg-[var(--surface-elevated)] border border-[var(--border-primary)] rounded-xl z-[500] animate-slideDown overflow-hidden"
    >
      {children}
    </div>,
    document.body
  );
};

// ─── Main component ───────────────────────────────────────────────────────────

type ActivePopover = 'date' | 'priority' | 'assignee' | 'tags' | 'reminder' | null;

export const TodoFormModal: React.FC<TodoFormModalProps> = ({
  isOpen,
  todo,
  members,
  isPersonalSpace,
  defaultColumnId,
  defaultDueDate,
  defaultTitle,
  position,
  restoreFromDraftOnOpen = false,
  contentTags = [],
  selectedContentTags = [],
  onContentTagsChange,
  onCreateContentTag,
  onSave,
  onToggleComplete,
  onDelete,
  onClose,
}) => {
  const [title, setTitle] = useState('');
  const [notes, setNotes] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [dueTime, setDueTime] = useState('');
  const [duration, setDuration] = useState<number | null>(null);
  const [assigneeId, setAssigneeId] = useState<string | null>(null);
  const [priority, setPriority] = useState<'none' | 'low' | 'medium' | 'high'>('none');
  const [isCompleted, setIsCompleted] = useState(false);

  const [reminderMinutesBefore, setReminderMinutesBefore] = useState<number | null>(null);
  const [customReminderExpanded, setCustomReminderExpanded] = useState(false);
  const [customReminderValue, setCustomReminderValue] = useState(30);
  const [customReminderUnit, setCustomReminderUnit] = useState<ReminderUnit>('minute');

  const [activePopover, setActivePopover] = useState<ActivePopover>(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [timeEnabled, setTimeEnabled] = useState(false);
  const timeFormat = useTimeFormat();
  const dateFormat = useDateFormat();
  const [popoverStyle, setPopoverStyle] = useState<React.CSSProperties>({});

  // Calendar view state
  const today = new Date();
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());
  const todayKey = toDateKey(today.getFullYear(), today.getMonth(), today.getDate());

  // Tag popover state
  const [tagQuery, setTagQuery] = useState('');
  const [creatingTag, setCreatingTag] = useState(false);

  const modalRef = useRef<HTMLDivElement>(null);
  const titleInputRef = useRef<HTMLInputElement>(null);
  useFocusTrap(modalRef, isOpen);

  // Chip button refs for popover anchoring
  const dateBtnRef = useRef<HTMLButtonElement>(null);
  const reminderBtnRef = useRef<HTMLButtonElement>(null);
  const priorityBtnRef = useRef<HTMLButtonElement>(null);
  const assigneeBtnRef = useRef<HTMLButtonElement>(null);
  const tagsBtnRef = useRef<HTMLButtonElement>(null);

  // ── Open/reset ────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!isOpen) return;
    if (todo) {
      setTitle(todo.title);
      setNotes(todo.notes || '');
      setDueDate(todo.due_date || '');
      setDueTime(todo.due_time || '');
      setDuration(todo.duration ?? null);
      setAssigneeId(todo.assignee_id);
      setPriority(todo.priority || 'none');
      setTimeEnabled(!!(todo.due_time));
      setReminderMinutesBefore(todo.reminder_minutes_before ?? null);
      setIsCompleted(!!(todo.completed_at));
    } else {
      setTitle(defaultTitle || '');
      setNotes('');
      setDueDate(defaultDueDate || '');
      setDueTime('');
      setDuration(null);
      setAssigneeId(null);
      setPriority('none');
      setTimeEnabled(false);
      setReminderMinutesBefore(null);
      setIsCompleted(false);
    }
    setActivePopover(null);
    setConfirmingDelete(false);
    setTimeout(() => titleInputRef.current?.focus(), 50);
  }, [isOpen, todo, defaultDueDate, defaultTitle]);

  // Restore draft
  useEffect(() => {
    if (!isOpen || !restoreFromDraftOnOpen || todo) return;
    const draft = loadTaskDraft();
    if (!draft?.title) return;
    setTitle(draft.title);
    setNotes(draft.notes);
    setDueDate(draft.dueDate || defaultDueDate || '');
    setDueTime(draft.dueTime || '');
    setAssigneeId(draft.assigneeId);
    setPriority(draft.priority || 'none');
    setTimeout(() => titleInputRef.current?.focus(), 50);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, restoreFromDraftOnOpen]);

  // Sync custom reminder fields when reminder popover opens
  useEffect(() => {
    if (activePopover !== 'reminder') return;
    const isPreset = reminderMinutesBefore !== null && REMINDER_PRESETS.some(p => p.value === reminderMinutesBefore);
    if (reminderMinutesBefore !== null && !isPreset) {
      const { value, unit } = minutesToCustomFields(reminderMinutesBefore);
      setCustomReminderValue(value);
      setCustomReminderUnit(unit);
      setCustomReminderExpanded(true);
    } else {
      setCustomReminderExpanded(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePopover]);

  // ── Popover helpers ───────────────────────────────────────────────────────

  const openPopover = useCallback((type: ActivePopover, btnRef: React.RefObject<HTMLButtonElement | null>) => {
    if (activePopover === type) { setActivePopover(null); return; }
    if (!btnRef.current) return;
    const rect = btnRef.current.getBoundingClientRect();
    const goUp = window.innerHeight - rect.bottom < 300;
    setPopoverStyle({
      position: 'fixed',
      left: Math.min(rect.left, window.innerWidth - 220),
      ...(goUp ? { bottom: window.innerHeight - rect.top + 4 } : { top: rect.bottom + 6 }),
    });
    setActivePopover(type);
  }, [activePopover]);

  // ── Save / dismiss ────────────────────────────────────────────────────────

  const handleDismiss = useCallback(() => {
    const hasDraftContent = !todo && (title.trim() || notes.trim() || dueDate || priority !== 'none' || assigneeId);
    if (hasDraftContent) {
      saveTaskDraft({
        title, notes, columnId: defaultColumnId || '', dueDate, dueTime,
        tagId: null, assigneeId, priority,
        dateEnabled: !!dueDate, timeEnabled: !!dueTime,
        categoryEnabled: false, priorityEnabled: priority !== 'none',
        assigneeEnabled: !!assigneeId,
      });
    }
    onClose();
  }, [todo, title, notes, defaultColumnId, dueDate, dueTime, assigneeId, priority, onClose]);

  const handleSave = useCallback(() => {
    if (!title.trim()) return;
    clearTaskDraft();
    onClose();
    onSave({
      title: title.trim(),
      notes: notes.trim() || null,
      column_id: todo?.column_id || defaultColumnId || '',
      due_date: dueDate || null,
      due_time: dueTime || null,
      duration: dueTime ? (duration ?? 30) : null,
      calendar_id: todo?.calendar_id ?? null,
      assignee_id: assigneeId,
      priority,
      reminder_minutes_before: reminderMinutesBefore,
      reminder_fire_at:
        reminderMinutesBefore !== null && (dueDate || dueTime)
          ? computeReminderFireAt(dueDate || new Date().toISOString().slice(0, 10), dueTime, reminderMinutesBefore)
          : null,
    });
  }, [title, notes, todo, defaultColumnId, dueDate, dueTime, duration, assigneeId, priority, reminderMinutesBefore, onClose, onSave]);

  const handleDelete = useCallback(async () => {
    if (!confirmingDelete) { setConfirmingDelete(true); return; }
    onClose();
    await onDelete();
  }, [confirmingDelete, onClose, onDelete]);

  // ── Keyboard ──────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (activePopover) { setActivePopover(null); return; }
        handleDismiss();
        return;
      }
      const activeEl = document.activeElement as HTMLElement | null;
      const inTagInput = !!activeEl?.closest?.('[data-tag-input-container]');
      if (e.key === 'Enter' && !e.shiftKey && activeEl?.tagName !== 'TEXTAREA' && !inTagInput) {
        e.preventDefault();
        handleSave();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, activePopover, handleDismiss, handleSave]);

  // ── Drag ──────────────────────────────────────────────────────────────────

  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [modalPosition, setModalPosition] = useState<{ left: number; top: number } | null>(null);

  useEffect(() => { if (isOpen) setModalPosition(null); }, [isOpen, position]);

  const isInteractive = useCallback((target: HTMLElement) => {
    const sel = ['input','textarea','button','select','a','[role="button"]'];
    let el: HTMLElement | null = target;
    while (el && el !== modalRef.current) {
      if (sel.some(s => el!.matches(s))) return true;
      el = el.parentElement;
    }
    return false;
  }, []);

  const handleDragStart = useCallback((e: React.MouseEvent) => {
    if (!modalRef.current || isInteractive(e.target as HTMLElement)) return;
    const rect = modalRef.current.getBoundingClientRect();
    setDragOffset({ x: e.clientX - rect.left, y: e.clientY - rect.top });
    setIsDragging(true);
    e.preventDefault();
  }, [isInteractive]);

  useEffect(() => {
    if (!isDragging) return;
    const onMove = (e: MouseEvent) => {
      const w = 380, h = modalRef.current?.offsetHeight || 320, p = 10;
      setModalPosition({
        left: Math.max(p, Math.min(e.clientX - dragOffset.x, window.innerWidth - w - p)),
        top: Math.max(p, Math.min(e.clientY - dragOffset.y, window.innerHeight - h - p)),
      });
    };
    const onUp = () => setIsDragging(false);
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); };
  }, [isDragging, dragOffset]);

  const isMobileView = window.innerWidth < 640;

  const calcStyle = (): React.CSSProperties => {
    if (isMobileView) return { left: 0, right: 0, bottom: 0, width: '100%', maxHeight: '85dvh', overflowY: 'auto', borderRadius: '16px 16px 0 0' };
    const w = 380, p = 20, vh = window.innerHeight, vw = window.innerWidth;
    if (modalPosition) return {
      left: `${modalPosition.left}px`, top: `${modalPosition.top}px`, width: `${w}px`,
      maxHeight: `${Math.max(300, vh - modalPosition.top - p)}px`,
    };
    if (position) {
      const left = Math.max(p, Math.min(position.x, vw - w - p));
      const anchorY = position.y;
      const spaceBelow = vh - anchorY - p;
      const spaceAbove = anchorY - p;
      if (spaceBelow >= spaceAbove) {
        return { left: `${left}px`, top: `${Math.max(p, anchorY)}px`, width: `${w}px`, maxHeight: `${Math.max(300, spaceBelow)}px` };
      }
      return { left: `${left}px`, bottom: `${Math.max(p, vh - anchorY)}px`, width: `${w}px`, maxHeight: `${Math.max(300, spaceAbove)}px` };
    }
    return {
      left: `${Math.max(p, (vw - w) / 2)}px`, top: '50%', transform: 'translateY(-50%)',
      width: `${w}px`, maxHeight: `${vh - 40}px`,
    };
  };

  // ── Tag popover helpers ───────────────────────────────────────────────────

  const trimmedTagQuery = tagQuery.trim().toLowerCase();
  const filteredTags = contentTags.filter(t => t.name.toLowerCase().includes(trimmedTagQuery));
  const exactTagMatch = contentTags.some(t => t.name.toLowerCase() === trimmedTagQuery);
  const showCreateTag = trimmedTagQuery.length >= 2 && !exactTagMatch;

  const is24h = timeFormat === '24h';

  const handleCreateTag = async () => {
    if (!trimmedTagQuery || creatingTag) return;
    setCreatingTag(true);
    try {
      const t = await onCreateContentTag?.(trimmedTagQuery);
      if (t) { onContentTagsChange?.([...selectedContentTags, t]); setTagQuery(''); }
    } finally { setCreatingTag(false); }
  };

  // Calendar grid
  const calTotalDays = daysInMonth(viewYear, viewMonth);
  const calStartOffset = firstWeekday(viewYear, viewMonth);
  const calCells: (number | null)[] = [
    ...Array(calStartOffset).fill(null),
    ...Array.from({ length: calTotalDays }, (_, i) => i + 1),
  ];
  while (calCells.length % 7 !== 0) calCells.push(null);

  // Derived display values
  const currentAssignee = members.find(m => m.user_id === assigneeId);
  const dateBadgeLabel = dueDate || dueTime
    ? dueDate && dueTime
      ? `${formatDateBadge(dueDate, dateFormat)}, ${formatTimeBadge(dueTime, is24h)}`
      : dueDate
      ? formatDateBadge(dueDate, dateFormat)
      : formatTimeBadge(dueTime, is24h)
    : null;

  if (!isOpen) return null;

  return (
    <div
      className={`fixed inset-0 z-[60] bg-[var(--modal-overlay)] animate-fadeInBackdrop ${isMobileView ? 'flex items-end' : ''}`}
      onMouseDown={(e) => {
        const target = e.target as HTMLElement;
        const onTagPortal = !!target.closest('[data-tag-input-portal]');
        if (modalRef.current && !modalRef.current.contains(target) && !onTagPortal) handleDismiss();
      }}
    >
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        className={`${isMobileView ? 'relative w-full' : 'absolute'} bg-[var(--modal-bg)] flex flex-col border border-[var(--border-primary)] ${
          isMobileView ? 'animate-slideUp rounded-t-2xl' : 'animate-fadeIn rounded-2xl overflow-hidden'
        } ${isDragging ? 'cursor-grabbing select-none' : 'cursor-default'}`}
        style={{ ...calcStyle(), boxShadow: 'var(--shadow-lg)' }}
        onMouseDown={isMobileView ? undefined : handleDragStart}
      >
        {/* Mobile drag handle */}
        {isMobileView && (
          <div className="flex justify-center pt-2 pb-1">
            <div className="w-10 h-1 rounded-full bg-[var(--content-tertiary)] opacity-40" />
          </div>
        )}

        {/* Close */}
        <button
          onClick={handleDismiss}
          className="absolute top-3 right-3 w-7 h-7 flex items-center justify-center rounded-lg text-[var(--content-tertiary)] hover:text-[var(--content-secondary)] hover:bg-[var(--surface-tertiary)] transition-colors z-10"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Body */}
        <div className="px-5 pt-5 pb-4 flex flex-col gap-0 overflow-y-auto scrollbar-styled flex-1 min-h-0">

          {/* Title row with completion circle */}
          <div className="flex items-start gap-2.5 pr-8">
            {todo && onToggleComplete && (
              <button
                type="button"
                onClick={async () => {
                  setIsCompleted(c => !c);
                  await onToggleComplete();
                }}
                className={`mt-1 flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${
                  isCompleted
                    ? 'bg-[var(--accent-primary)] border-[var(--accent-primary)]'
                    : 'border-[var(--border-primary)] hover:border-[var(--accent-primary)]'
                }`}
                title={isCompleted ? 'Mark incomplete' : 'Mark complete'}
              >
                {isCompleted && <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />}
              </button>
            )}
            <input
              ref={titleInputRef}
              type="text"
              value={title}
              onChange={(e) => { const v = e.target.value; setTitle(v.length === 1 ? v.toUpperCase() : v); }}
              placeholder="Task name"
              maxLength={200}
              className={`flex-1 bg-transparent text-[var(--content-primary)] placeholder-[var(--content-tertiary)] text-lg font-semibold focus:outline-none pb-1 ${isCompleted ? 'line-through text-[var(--content-tertiary)]' : ''}`}
            />
          </div>

          {/* Metadata chips row */}
          <div className="flex flex-wrap gap-1 mt-2 -ml-1">
            {/* Due date + time (combined) */}
            <MetaChip
              ref={dateBtnRef}
              icon={<Calendar className="w-3.5 h-3.5 flex-shrink-0" />}
              label={dateBadgeLabel}
              active={!!(dueDate || dueTime)}
              placeholder="Due date"
              onClick={() => openPopover('date', dateBtnRef)}
            />

            {/* Reminder — only available when a due date + time are set */}
            {dueDate && dueTime && (
              <MetaChip
                ref={reminderBtnRef}
                icon={<Bell className="w-3.5 h-3.5 flex-shrink-0" />}
                label={reminderMinutesBefore !== null ? formatReminderChip(reminderMinutesBefore) : null}
                active={reminderMinutesBefore !== null}
                placeholder="Reminder"
                onClick={() => openPopover('reminder', reminderBtnRef)}
              />
            )}

            {/* Priority */}
            <MetaChip
              ref={priorityBtnRef}
              icon={<Flag className="w-3.5 h-3.5 flex-shrink-0" />}
              label={priority !== 'none' ? PRIORITY[priority].label : null}
              active={priority !== 'none'}
              activeColor={priority !== 'none' ? PRIORITY[priority].color : undefined}
              placeholder="Priority"
              onClick={() => openPopover('priority', priorityBtnRef)}
            />

            {/* Assignee */}
            {!isPersonalSpace && members.length > 0 && (
              <MetaChip
                ref={assigneeBtnRef}
                icon={<User className="w-3.5 h-3.5 flex-shrink-0" />}
                label={currentAssignee ? (currentAssignee.display_name || currentAssignee.email.split('@')[0]) : null}
                active={!!assigneeId}
                placeholder="Assignee"
                onClick={() => openPopover('assignee', assigneeBtnRef)}
              />
            )}

            {/* Tags — individual removable chips + add button */}
            {onContentTagsChange && (
              <>
                {selectedContentTags.map(tag => (
                  <span key={tag.id} className="inline-flex items-center gap-1 h-7 px-2.5 rounded-lg text-xs border border-[var(--border-primary)] bg-[var(--surface-tertiary)] text-[var(--content-secondary)]">
                    <Tag className="w-3.5 h-3.5 flex-shrink-0 text-[var(--content-tertiary)]" />
                    {tag.name}
                    <button
                      type="button"
                      onMouseDown={(e) => { e.preventDefault(); onContentTagsChange(selectedContentTags.filter(t => t.id !== tag.id)); }}
                      className="ml-0.5 hover:text-[var(--content-primary)] transition-colors"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
                <MetaChip
                  ref={tagsBtnRef}
                  icon={<Tag className="w-3.5 h-3.5 flex-shrink-0" />}
                  label={null}
                  active={false}
                  placeholder={selectedContentTags.length > 0 ? 'Add tag' : 'Tags'}
                  onClick={() => openPopover('tags', tagsBtnRef)}
                />
              </>
            )}
          </div>

          {/* Notes */}
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Add notes…"
            maxLength={5000}
            rows={3}
            className="w-full mt-4 bg-transparent text-[var(--content-primary)] placeholder-[var(--content-tertiary)] text-sm focus:outline-none resize-none leading-relaxed"
          />

          {/* Footer row */}
          <div className="flex items-center justify-between mt-4 pt-3 border-t border-[var(--border-secondary)] flex-shrink-0">
            {/* Delete */}
            {todo ? (
              confirmingDelete ? (
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleDelete}
                    className="text-xs text-[var(--status-error-text)] hover:underline transition-colors"
                  >
                    Confirm
                  </button>
                  <button
                    onClick={() => setConfirmingDelete(false)}
                    className="text-xs text-[var(--content-tertiary)] hover:text-[var(--content-secondary)] transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <button
                  onClick={handleDelete}
                  title="Delete task"
                  className="w-7 h-7 flex items-center justify-center rounded-lg text-[var(--content-tertiary)] hover:text-[var(--status-error-text)] hover:bg-[var(--status-error-bg)] transition-colors"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )
            ) : <div />}

            {/* Metadata: created / completed dates */}
            {todo && (
              <span className="text-[10px] text-[var(--content-tertiary)] text-center leading-tight px-1">
                {(() => {
                  const createdDateKey = todo.created_at.slice(0, 10);
                  const parts: string[] = [`Created ${formatShortDate(createdDateKey, dateFormat)}`];
                  if (todo.completed_at) {
                    parts.push(`Completed ${formatShortDate(todo.completed_at.slice(0, 10), dateFormat)}`);
                  }
                  return parts.join(' · ');
                })()}
              </span>
            )}

            {/* Save */}
            <button
              onClick={handleSave}
              disabled={!title.trim()}
              className="btn-primary !w-auto px-4 py-1.5 text-xs disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {todo ? 'Save' : 'Add'}
            </button>
          </div>
        </div>
      </div>

      {/* ── Date + Time popover ────────────────────────────────────────────── */}
      {activePopover === 'date' && (
        <Popover style={popoverStyle} width={240} onClose={() => setActivePopover(null)} ignoreRef={dateBtnRef as React.RefObject<HTMLElement | null>}>
          {/* Month nav */}
          <div className="flex items-center justify-between px-3 py-2">
            <span className="text-xs font-semibold text-[var(--content-primary)]">{MONTHS[viewMonth]} {viewYear}</span>
            <div className="flex items-center gap-0.5">
              <button type="button" onClick={() => { if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y-1); } else setViewMonth(m => m-1); }}
                className="h-5 w-5 flex items-center justify-center rounded hover:bg-[var(--sidebar-hover)] transition-colors">
                <ChevronLeft className="w-3 h-3 text-[var(--content-secondary)]" />
              </button>
              <button type="button" onClick={() => { setViewYear(today.getFullYear()); setViewMonth(today.getMonth()); }} title="Today"
                className="h-5 w-5 flex items-center justify-center rounded hover:bg-[var(--sidebar-hover)] text-[var(--content-tertiary)] transition-colors text-[11px] leading-none">
                ●
              </button>
              <button type="button" onClick={() => { if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y+1); } else setViewMonth(m => m+1); }}
                className="h-5 w-5 flex items-center justify-center rounded hover:bg-[var(--sidebar-hover)] transition-colors">
                <ChevronRight className="w-3 h-3 text-[var(--content-secondary)]" />
              </button>
            </div>
          </div>
          {/* Day headers */}
          <div className="grid grid-cols-7 px-2">
            {DAYS.map(d => <div key={d} className="h-6 flex items-center justify-center text-[10px] font-medium text-[var(--content-tertiary)]">{d}</div>)}
          </div>
          {/* Day grid */}
          <div className="grid grid-cols-7 px-2 pb-2">
            {calCells.map((day, i) => {
              if (!day) return <div key={i} />;
              const key = toDateKey(viewYear, viewMonth, day);
              return (
                <button key={i} type="button" onClick={() => setDueDate(key)}
                  className={`h-6 w-6 mx-auto flex items-center justify-center rounded-full text-xs transition-colors ${
                    key === dueDate ? 'bg-[var(--accent-primary)] text-white font-semibold'
                    : key === todayKey ? 'bg-[var(--accent-subtle)] text-[var(--accent-primary)] font-semibold'
                    : 'text-[var(--content-primary)] hover:bg-[var(--sidebar-hover)]'
                  }`}
                >{day}</button>
              );
            })}
          </div>
          {/* Date text input */}
          <div className="px-3 pb-2.5 pt-2 border-t border-[var(--border-secondary)]" data-no-drag>
            <DateTextInput
              value={dueDate}
              onChange={setDueDate}
              className="w-full px-2.5 py-1.5 bg-[var(--input-bg)] rounded-lg text-xs text-right text-[var(--content-primary)] placeholder-[var(--content-tertiary)] focus:outline-none focus:ring-1 focus:ring-[var(--accent-primary)]"
            />
          </div>
          {/* Time toggle section */}
          <div className="px-3 pb-3 border-t border-[var(--border-secondary)] pt-2.5">
            <div className="flex items-center justify-between">
              <span className="text-xs text-[var(--content-secondary)]">Time</span>
              <button
                type="button"
                onClick={() => {
                  if (timeEnabled) {
                    setTimeEnabled(false);
                    setDueTime('');
                    setDuration(null);
                    setReminderMinutesBefore(null);
                  } else {
                    setTimeEnabled(true);
                    const t = getRoundedTime();
                    setDueTime(t);
                    setDuration(30);
                    if (!dueDate || dueDate === getTodayKey()) setDueDate(autoDateForTime(t));
                  }
                }}
                className={`relative w-9 h-5 rounded-full transition-colors flex-shrink-0 ${timeEnabled ? 'bg-[var(--accent-primary)]' : 'bg-[var(--border-primary)]'}`}
              >
                <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${timeEnabled ? 'translate-x-4' : 'translate-x-0'}`} />
              </button>
            </div>
            {timeEnabled && dueTime && (
              <div className="mt-2.5 space-y-2" data-no-drag>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs text-[var(--content-secondary)]">Starts</span>
                  <InlineTimeInput
                    value={dueTime}
                    onChange={(t) => {
                      setDueTime(t);
                      if (!dueDate || dueDate === getTodayKey()) setDueDate(autoDateForTime(t));
                    }}
                    timeFormat={timeFormat}
                  />
                </div>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs text-[var(--content-secondary)]">Ends</span>
                  <EndTimeInput
                    value={addMinutesToTime(dueTime, duration ?? 30)}
                    startTime={dueTime}
                    onChange={(endT) => {
                      const [sh, sm] = dueTime.split(':').map(Number);
                      const [eh, em] = endT.split(':').map(Number);
                      let diff = (eh * 60 + em) - (sh * 60 + sm);
                      if (diff <= 0) diff += 24 * 60;
                      setDuration(Math.min(diff, 480));
                    }}
                    timeFormat={timeFormat}
                    stepMinutes={15}
                    maxOffsetMinutes={240}
                  />
                </div>
              </div>
            )}
          </div>
          {/* Clear / Done row */}
          {(dueDate || dueTime) && (
            <div className="px-3 pb-2.5 flex items-center gap-3 border-t border-[var(--border-secondary)] pt-2">
              <button type="button" onClick={() => { setDueDate(''); setDueTime(''); setDuration(null); setTimeEnabled(false); setReminderMinutesBefore(null); }}
                className="text-xs text-[var(--content-tertiary)] hover:text-[var(--status-error-text)] transition-colors">
                Clear all
              </button>
              <button type="button" onClick={() => setActivePopover(null)}
                className="text-xs text-[var(--accent-primary)] hover:underline transition-colors">
                Done
              </button>
            </div>
          )}
        </Popover>
      )}

      {/* ── Reminder popover ──────────────────────────────────────────────── */}
      {activePopover === 'reminder' && (
        <Popover style={popoverStyle} width={220} onClose={() => setActivePopover(null)} ignoreRef={reminderBtnRef as React.RefObject<HTMLElement | null>}>
          {/* Presets */}
          {REMINDER_PRESETS.map(preset => (
            <button
              key={preset.value}
              type="button"
              onClick={() => { setReminderMinutesBefore(preset.value); setCustomReminderExpanded(false); setActivePopover(null); }}
              className={`w-full flex items-center justify-between px-3 py-2 text-xs hover:bg-[var(--sidebar-hover)] transition-colors ${
                reminderMinutesBefore === preset.value ? 'text-[var(--accent-primary)]' : 'text-[var(--content-primary)]'
              }`}
            >
              {preset.label}
              {reminderMinutesBefore === preset.value && <Check className="w-3 h-3 flex-shrink-0" />}
            </button>
          ))}

          {/* Custom */}
          <div className="border-t border-[var(--border-secondary)] mt-1 pt-1">
            <button
              type="button"
              onClick={() => setCustomReminderExpanded(e => !e)}
              className={`w-full flex items-center justify-between px-3 py-2 text-xs hover:bg-[var(--sidebar-hover)] transition-colors ${
                reminderMinutesBefore !== null && !REMINDER_PRESETS.some(p => p.value === reminderMinutesBefore)
                  ? 'text-[var(--accent-primary)]'
                  : 'text-[var(--content-primary)]'
              }`}
            >
              Custom
              {reminderMinutesBefore !== null && !REMINDER_PRESETS.some(p => p.value === reminderMinutesBefore) && (
                <Check className="w-3 h-3 flex-shrink-0" />
              )}
            </button>

            {customReminderExpanded && (
              <div className="px-3 pb-3 flex items-center gap-2">
                <input
                  type="number"
                  min={1}
                  max={1000}
                  value={customReminderValue}
                  onChange={(e) => {
                    const v = Math.max(1, Math.min(1000, parseInt(e.target.value) || 1));
                    setCustomReminderValue(v);
                    setReminderMinutesBefore(v * UNIT_MINUTES[customReminderUnit]);
                  }}
                  className="w-14 px-2 py-1.5 bg-[var(--input-bg)] rounded-lg text-xs text-center text-[var(--content-primary)] focus:outline-none focus:ring-1 focus:ring-[var(--accent-primary)]"
                />
                <select
                  value={customReminderUnit}
                  onChange={(e) => {
                    const unit = e.target.value as ReminderUnit;
                    setCustomReminderUnit(unit);
                    setReminderMinutesBefore(customReminderValue * UNIT_MINUTES[unit]);
                  }}
                  className="flex-1 px-2 py-1.5 bg-[var(--input-bg)] rounded-lg text-xs text-[var(--content-primary)] focus:outline-none focus:ring-1 focus:ring-[var(--accent-primary)]"
                >
                  <option value="minute">min</option>
                  <option value="hour">hour</option>
                  <option value="day">day</option>
                  <option value="week">week</option>
                  <option value="month">month</option>
                </select>
                <span className="text-xs text-[var(--content-tertiary)] flex-shrink-0">before</span>
              </div>
            )}
          </div>

          {/* Clear */}
          {reminderMinutesBefore !== null && (
            <div className="border-t border-[var(--border-secondary)] mt-1 pt-1 pb-1">
              <button
                type="button"
                onClick={() => { setReminderMinutesBefore(null); setActivePopover(null); }}
                className="w-full flex items-center px-3 py-2 text-xs text-[var(--status-error-text)] hover:bg-[var(--status-error-bg)] transition-colors"
              >
                Clear reminder
              </button>
            </div>
          )}
        </Popover>
      )}

      {/* ── Priority popover ───────────────────────────────────────────────── */}
      {activePopover === 'priority' && (
        <Popover style={popoverStyle} width={150} onClose={() => setActivePopover(null)} ignoreRef={priorityBtnRef as React.RefObject<HTMLElement | null>}>
          <button type="button"
            onClick={() => { setPriority('none'); setActivePopover(null); }}
            className="w-full px-3 py-1.5 text-xs text-left flex items-center justify-between hover:bg-[var(--sidebar-hover)] transition-colors text-[var(--content-secondary)]"
          >
            None
            {priority === 'none' && <Check className="w-3 h-3 flex-shrink-0" />}
          </button>
          {PRIORITY_ORDER.map(p => (
            <button key={p} type="button"
              onClick={() => { setPriority(p); setActivePopover(null); }}
              style={{ color: PRIORITY[p].color }}
              className="w-full px-3 py-1.5 text-xs text-left flex items-center justify-between hover:bg-[var(--sidebar-hover)] transition-colors"
            >
              {PRIORITY[p].label}
              {priority === p && <Check className="w-3 h-3 flex-shrink-0" />}
            </button>
          ))}
        </Popover>
      )}

      {/* ── Assignee popover ───────────────────────────────────────────────── */}
      {activePopover === 'assignee' && (
        <Popover style={popoverStyle} width={190} onClose={() => setActivePopover(null)} ignoreRef={assigneeBtnRef as React.RefObject<HTMLElement | null>}>
          {assigneeId && (
            <button type="button" onClick={() => { setAssigneeId(null); setActivePopover(null); }} className="w-full px-3 py-1.5 text-xs text-left text-[var(--content-tertiary)] hover:bg-[var(--sidebar-hover)] border-b border-[var(--border-secondary)]">
              Unassign
            </button>
          )}
          {members.map(m => (
            <button key={m.user_id} type="button"
              onClick={() => { setAssigneeId(m.user_id); setActivePopover(null); }}
              className={`w-full px-3 py-1.5 text-xs text-left flex items-center justify-between hover:bg-[var(--sidebar-hover)] transition-colors ${m.user_id === assigneeId ? 'text-[var(--accent-primary)]' : 'text-[var(--content-primary)]'}`}
            >
              {m.display_name || m.email.split('@')[0]}
              {m.user_id === assigneeId && <Check className="w-3 h-3 flex-shrink-0" />}
            </button>
          ))}
        </Popover>
      )}

      {/* ── Tags popover ───────────────────────────────────────────────────── */}
      {activePopover === 'tags' && onContentTagsChange && (
        <Popover style={popoverStyle} width={200} onClose={() => { setActivePopover(null); setTagQuery(''); }} ignoreRef={tagsBtnRef as React.RefObject<HTMLElement | null>}>
          <div className="p-1.5">
            <input
              type="text"
              value={tagQuery}
              onChange={(e) => setTagQuery(e.target.value)}
              placeholder="Search tags…"
              autoFocus
              data-tag-input-container
              className="w-full px-2 py-1 text-xs bg-[var(--surface-secondary)] rounded-lg text-[var(--content-primary)] placeholder-[var(--content-tertiary)] focus:outline-none mb-0.5"
              onKeyDown={(e) => {
                e.stopPropagation();
                if (e.key === 'Enter') {
                  e.preventDefault();
                  if (filteredTags.length > 0) {
                    const tag = filteredTags[0];
                    const isSel = selectedContentTags.some(t => t.id === tag.id);
                    onContentTagsChange(isSel ? selectedContentTags.filter(t => t.id !== tag.id) : [...selectedContentTags, tag]);
                  } else if (showCreateTag) handleCreateTag();
                }
                if (e.key === 'Escape') { setActivePopover(null); setTagQuery(''); }
              }}
            />
          </div>
          {filteredTags.length === 0 && !showCreateTag && (
            <p className="px-3 py-1.5 text-xs text-[var(--content-tertiary)]">{tagQuery.trim() ? 'No matches' : 'No tags yet'}</p>
          )}
          {filteredTags.map(tag => {
            const isSel = selectedContentTags.some(t => t.id === tag.id);
            return (
              <button key={tag.id} type="button"
                onClick={() => onContentTagsChange(isSel ? selectedContentTags.filter(t => t.id !== tag.id) : [...selectedContentTags, tag])}
                className="w-full px-3 py-1.5 text-xs text-left flex items-center gap-1.5 hover:bg-[var(--sidebar-hover)] transition-colors text-[var(--content-primary)]"
              >
                <Tag className="w-3 h-3 text-[var(--content-tertiary)] flex-shrink-0" />
                <span className="flex-1">{tag.name}</span>
                <div className={`w-3.5 h-3.5 rounded border flex items-center justify-center flex-shrink-0 transition-colors ${
                  isSel ? 'bg-[var(--accent-primary)] border-[var(--accent-primary)]' : 'border-[var(--content-tertiary)]'
                }`}>
                  {isSel && <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />}
                </div>
              </button>
            );
          })}
          {showCreateTag && (
            <button type="button" onClick={handleCreateTag} disabled={creatingTag}
              className="w-full px-3 py-1.5 text-xs text-left flex items-center gap-1.5 hover:bg-[var(--sidebar-hover)] text-[var(--accent-primary)] border-t border-[var(--border-secondary)] disabled:opacity-50"
            >
              <Plus className="w-3 h-3 flex-shrink-0" />
              {creatingTag ? 'Adding…' : `Add "${trimmedTagQuery}"`}
            </button>
          )}
        </Popover>
      )}
    </div>
  );
};
